import { useState } from 'react';
import { RepeatIcon, TriangleAlertIcon } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@tinct/web-components';
import { updateLandingPageDomainSchema } from '@tinct/core-api-models';

export type ReplaceDomainModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentDomain: string;
  /** Server-side rejection message (400 invalid domain / 409 already taken). */
  errorMessage?: string | null;
  isReplacing?: boolean;
  onConfirm: (domain: string) => void;
};

export function ReplaceDomainModal({
  open,
  onOpenChange,
  currentDomain,
  errorMessage,
  isReplacing,
  onConfirm,
}: ReplaceDomainModalProps) {
  const [domain, setDomain] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleConfirm = () => {
    const parsed = updateLandingPageDomainSchema.safeParse({
      domain: domain.trim().toLowerCase(),
    });
    if (!parsed.success) {
      setValidationError(parsed.error.issues[0]?.message ?? 'Invalid domain');
      return;
    }
    setValidationError(null);
    onConfirm(parsed.data.domain);
  };

  const displayedError = validationError ?? errorMessage;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Replace {currentDomain}</DialogTitle>
        </DialogHeader>
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
          <TriangleAlertIcon className="mt-0.5 size-4 shrink-0 text-amber-600" />
          <div className="flex flex-1 flex-col gap-1 text-sm text-amber-700">
            <span>
              The current domain is dropped immediately and provisioning
              restarts on the new one — its URLs only come back once the new
              domain is active. Tinct URLs stay live throughout.
            </span>
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <Input
            type="text"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleConfirm()}
            placeholder="lp.your-domain.com"
          />
          {displayedError && (
            <p className="text-sm text-destructive">{displayedError}</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!domain.trim()}
            isLoading={isReplacing}
          >
            <RepeatIcon className="size-4" />
            Replace domain
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
